import { useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { getApiAuthHeaders } from '../services/api';
import { apiPath } from '../utils/apiBaseUrl';

const RECONNECT_MS = 8000;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Conexão SSE (fetch + stream) para invalidar queries quando o backend publicar mudanças.
 * Não usa EventSource nativo para permitir Authorization e X-Tenant-Org-Id.
 */
export function useNotificationSSE(enabled) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return undefined;

    const token = localStorage.getItem('authToken');
    if (!token) return undefined;

    let cancelled = false;
    let abortCurrent = () => {};

    const invalidate = () => {
      queryClient.invalidateQueries(['notifications-unread-count']);
      queryClient.invalidateQueries(['notifications-header-dropdown']);
      queryClient.invalidateQueries(['notifications-in-app']);
      queryClient.invalidateQueries(['notifications-in-app-preview']);
    };

    const loop = async () => {
      while (!cancelled) {
        const ac = new AbortController();
        abortCurrent = () => ac.abort();
        try {
          const res = await fetch(apiPath('/api/v1/notifications/in-app/stream'), {
            headers: getApiAuthHeaders(),
            signal: ac.signal,
          });
          if (!res.ok) {
            if (!cancelled) await sleep(RECONNECT_MS);
            continue;
          }
          const reader = res.body?.getReader();
          if (!reader) {
            if (!cancelled) await sleep(RECONNECT_MS);
            continue;
          }

          const dec = new TextDecoder();
          let buf = '';
          while (!cancelled) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += dec.decode(value, { stream: true });
            const lines = buf.split('\n');
            buf = lines.pop() || '';
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const data = JSON.parse(line.slice(6));
                if (data.event === 'notifications_changed') {
                  invalidate();
                }
              } catch {
                /* ignore */
              }
            }
          }
        } catch (e) {
          if (e?.name === 'AbortError' || cancelled) return;
        }

        if (cancelled) return;
        await sleep(RECONNECT_MS);
      }
    };

    loop();

    return () => {
      cancelled = true;
      abortCurrent();
    };
  }, [enabled, queryClient]);
}
